import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { Plus, Edit2, Trash2, Tag, TrendingUp, TrendingDown } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL || window.location.origin}/api`;

function CategoriesManagement({ currentUser, isGlobalMode = false }) {
  const [expenseCategories, setExpenseCategories] = useState([]);
  const [revenueCategories, setRevenueCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentCategory, setCurrentCategory] = useState(null);
  const [activeTab, setActiveTab] = useState('expense');
  
  const [formData, setFormData] = useState({
    name: '',
    type: 'expense',
    color: '#ef4444',
    icon: 'tag'
  });

  const headers = { 'X-User-Id': currentUser?.id || '' };

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const globalParam = isGlobalMode ? '&global_only=true' : '';
      const [expenseRes, revenueRes] = await Promise.all([
        axios.get(`${API}/categories?type=expense${globalParam}`, { headers }),
        axios.get(`${API}/categories?type=revenue${globalParam}`, { headers })
      ]);
      setExpenseCategories(expenseRes.data);
      setRevenueCategories(revenueRes.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des catégories');
    } finally {
      setLoading(false);
    }
  };

  const openDialog = (category = null, type = 'expense') => {
    if (category) {
      setEditMode(true);
      setCurrentCategory(category);
      setFormData({
        name: category.name,
        type: category.type,
        color: category.color || '#ef4444',
        icon: category.icon || 'tag'
      });
    } else {
      setEditMode(false);
      setCurrentCategory(null);
      setFormData({
        name: '',
        type: type,
        color: '#ef4444',
        icon: 'tag'
      });
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error('Le nom de la catégorie est requis');
      return;
    }

    try {
      // Préparer le payload en enlevant les champs vides
      const payload = {
        name: formData.name.trim(),
        type: formData.type,
        color: formData.color || '#ef4444',
        icon: formData.icon || 'tag'
      };
      
      // N'inclure parent_category que si elle a une valeur
      if (formData.parent_category && formData.parent_category.trim()) {
        payload.parent_category = formData.parent_category;
      }

      if (editMode && currentCategory) {
        // Update existing category
        await axios.put(`${API}/categories/${currentCategory.id}`, payload, { headers });
        toast.success('Catégorie modifiée avec succès');
      } else {
        // Create new category
        const createPayload = { ...payload };
        if (isGlobalMode) {
          createPayload.pharmacy_id = null; // Explicitly make it global
        }
        await axios.post(`${API}/categories`, createPayload, { headers });
        toast.success('Catégorie créée avec succès');
      }
      
      setDialogOpen(false);
      fetchCategories();
    } catch (error) {
      if (error.response?.status === 403) {
        console.warn('Accès refusé. Droits administrateur requis.');
      } else {
        console.error('Erreur:', error.response?.data);
        toast.error(editMode ? 'Erreur lors de la modification' : 'Erreur lors de la création');
      }
    }
  };

  const handleDelete = async (category) => {
    if (!window.confirm(`Supprimer la catégorie "${category.name}" ?`)) return;

    try {
      await axios.delete(`${API}/categories/${category.id}`, { headers });
      toast.success('Catégorie supprimée');
      fetchCategories();
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error(error.response.data.detail || 'Cette catégorie est utilisée et ne peut être supprimée');
      } else if (error.response?.status === 403) {
        console.warn('Accès refusé. Droits administrateur requis.');
      } else {
        toast.error('Erreur lors de la suppression');
      }
    }
  };

  const CategoryCard = ({ category, type }) => {
    const isExpense = type === 'expense';
    const color = category.color || (isExpense ? '#ef4444' : '#10b981');
    
    return (
      <Card className="relative overflow-hidden group hover:shadow-2xl hover:-translate-y-1 transition-all duration-300 border-0 bg-white ring-1 ring-slate-100 cursor-default rounded-xl">
        {/* Subtle background gradient blob based on color */}
        <div 
          className="absolute -right-8 -top-8 w-24 h-24 rounded-full opacity-10 transition-transform duration-500 group-hover:scale-150"
          style={{ backgroundColor: color }}
        />
        
        <CardContent className="p-5 relative z-10">
          <div className="flex flex-col gap-4">
            <div className="flex items-start justify-between">
              <div 
                className="w-12 h-12 rounded-xl flex items-center justify-center shadow-sm transition-transform duration-300 group-hover:rotate-6"
                style={{ 
                  background: `linear-gradient(135deg, ${color}22 0%, ${color}11 100%)`,
                  border: `1px solid ${color}33`
                }}
              >
                <Tag 
                  className="w-5 h-5" 
                  style={{ color: color }}
                />
              </div>
              
              <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => openDialog(category, type)}
                  className="h-8 w-8 p-0 rounded-full hover:bg-slate-100 hover:text-blue-600 transition-colors"
                >
                  <Edit2 className="w-4 h-4" />
                </Button>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => handleDelete(category)}
                  className="h-8 w-8 p-0 rounded-full hover:bg-red-50 hover:text-red-600 transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
            
            <div>
              <h3 className="font-bold text-slate-800 text-lg leading-tight group-hover:text-black transition-colors">{category.name}</h3>
              <div className="flex items-center gap-2 mt-2">
                <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium" style={{ backgroundColor: `${color}15`, color: color }}>
                  {isExpense ? 'Dépense' : 'Revenu'}
                </span>
                {category.parent_category && (
                  <span className="text-xs text-slate-500 font-medium">
                    • {category.parent_category}
                  </span>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {!isGlobalMode && (
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Gestion des Catégories</h1>
            <p className="text-gray-500 mt-1 text-sm sm:text-base">Personnalisez les catégories de revenus et dépenses</p>
          </div>
        </div>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-2 max-w-md">
          <TabsTrigger value="expense" className="flex items-center gap-2">
            <TrendingDown className="w-4 h-4" />
            Dépenses ({expenseCategories.length})
          </TabsTrigger>
          <TabsTrigger value="revenue" className="flex items-center gap-2">
            <TrendingUp className="w-4 h-4" />
            Revenus ({revenueCategories.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="expense" className="space-y-4 mt-6">
          <div className="flex justify-end">
            <Button 
              onClick={() => openDialog(null, 'expense')}
              className="bg-gradient-to-r from-green-500 to-teal-400 hover:from-green-600 hover:to-teal-500"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nouvelle Catégorie
            </Button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {expenseCategories.map(cat => (
              <CategoryCard key={cat.id} category={cat} type="expense" />
            ))}
          </div>

          {expenseCategories.length === 0 && (
            <Card>
              <CardContent className="p-12 text-center text-slate-500">
                Aucune catégorie de dépense
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="revenue" className="space-y-4 mt-6">
          <div className="flex justify-end">
            <Button 
              onClick={() => openDialog(null, 'revenue')}
              className="bg-gradient-to-r from-green-500 to-teal-400 hover:from-green-600 hover:to-teal-500"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nouvelle Catégorie
            </Button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {revenueCategories.map(cat => (
              <CategoryCard key={cat.id} category={cat} type="revenue" />
            ))}
          </div>

          {revenueCategories.length === 0 && (
            <Card>
              <CardContent className="p-12 text-center text-slate-500">
                Aucune catégorie de revenu
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Edit/Create Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editMode ? 'Modifier la Catégorie' : 'Nouvelle Catégorie'}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="name">Nom de la catégorie *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Ex: Médicaments"
                required
              />
            </div>

            <div>
              <Label htmlFor="type">Type *</Label>
              <select
                id="type"
                value={formData.type}
                onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                required
              >
                <option value="expense">Dépense</option>
                <option value="revenue">Revenu</option>
              </select>
            </div>

            <div>
              <Label htmlFor="parent_category">Catégorie Parente</Label>
              <select
                id="parent_category"
                value={formData.parent_category || ''}
                onChange={(e) => setFormData({ ...formData, parent_category: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
              >
                <option value="">Aucune (Catégorie principale)</option>
                <option value="Achat">Achat</option>
                <option value="Charge">Charge</option>
                <option value="Salaire">Salaire</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">
                Si vous sélectionnez une catégorie parente, cette catégorie apparaîtra comme sous-catégorie
              </p>
            </div>

            <div className="flex gap-4 justify-end pt-4">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Annuler
              </Button>
              <Button 
                type="submit"
                className="bg-gradient-to-r from-green-500 to-teal-400 hover:from-green-600 hover:to-teal-500"
              >
                {editMode ? 'Modifier' : 'Créer'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default CategoriesManagement;
